import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Rnd } from 'react-rnd';
import { CardElementContent } from '../components/common/CardRenderer';
import api from '../api/axios';
import { useChurch } from '../context/ChurchContext';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/common/ConfirmModal';

const MM_TO_PX = 3.78; // ~96dpi, juste pour l'affichage écran de l'éditeur (l'export réel utilisera width_mm/height_mm)
const PANEL = { background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '10px' };
const BTN_DARK = { background: '#1e293b', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: 700, cursor: 'pointer' };
const BTN_GHOST = { background: 'var(--page-bg)', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: '6px', fontWeight: 600, cursor: 'pointer' };
const LABEL = { display: 'block', fontSize: '0.72rem', fontWeight: 600, marginBottom: '4px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.03em' };
const INPUT = { width: '100%', padding: '7px 9px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '0.82rem', background: '#fff', color: 'var(--text-primary)', boxSizing: 'border-box' };

const ADD_ITEMS = [
  { type: 'text', label: 'Texte libre', icon: '𝐓' },
  { type: 'photo', label: 'Photo du fidèle', icon: '🖼' },
  { type: 'image', label: 'Image / Logo', icon: '🏷' },
  { type: 'qrcode', label: 'QR Code', icon: '▦' },
  { type: 'shape', label: 'Forme', icon: '▭' },
];

const FIELD_ITEMS_FALLBACK = [
  { key: 'full_name', label: 'Nom complet' },
  { key: 'first_name', label: 'Prénom' },
  { key: 'last_name', label: 'Nom' },
  { key: 'member_code', label: 'Matricule' },
  { key: 'phone', label: 'Téléphone' },
  { key: 'email', label: 'Email' },
  { key: 'birth_date', label: 'Date de naissance' },
  { key: 'joined_date', label: "Date d'inscription" },
  { key: 'church_name', label: 'Église' },
  { key: 'family_name', label: 'Famille RCC' },
  { key: 'status_label', label: 'Statut' },
];

function defaultsFor(type) {
  switch (type) {
    case 'text': return { width: 40, height: 10, content: 'Texte', style: { fontSize: 14, color: '#1e293b', fontWeight: 'normal', align: 'left' } };
    case 'field': return { width: 40, height: 10, style: { fontSize: 14, color: '#1e293b', fontWeight: 'normal', align: 'left' } };
    case 'photo': return { width: 25, height: 40, style: { borderRadius: 'square', borderColor: '#1e40af', borderWidth: 2 } };
    case 'image': return { width: 20, height: 20, style: { opacity: 1, fit: 'cover' } };
    case 'qrcode': return { width: 20, height: 20, field_key: 'member_code', style: { fgColor: '#000000' } };
    case 'shape': return { width: 30, height: 15, style: { shape: 'rectangle', background: '#e2e8f0', borderColor: '#94a3b8', borderWidth: 1, opacity: 1 } };
    default: return { width: 20, height: 10, style: {} };
  }
}

export default function CardTemplateEditor() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { activeChurchId, isPlatformAdmin } = useChurch();

  const [template, setTemplate] = useState(null);
  const [activeSideId, setActiveSideId] = useState(null);
  const [activeSideKey, setActiveSideKey] = useState('front');
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [pendingSaves, setPendingSaves] = useState(0);
  const [saveError, setSaveError] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [fieldChoices, setFieldChoices] = useState(FIELD_ITEMS_FALLBACK);
  const [qrSources, setQrSources] = useState([{ key: 'member_code', label: 'Matricule (lisible)' }, { key: 'qr_code', label: 'Code QR interne' }]);
  const [participants, setParticipants] = useState([]);
  const [previewParticipantId, setPreviewParticipantId] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [confirmRollback, setConfirmRollback] = useState(null);
  const canvasRef = useRef(null);
  // Historique undo/redo : chaque entrée décrit comment revenir en arrière
  // ({type:'update', id, before, after}) ou recréer un élément supprimé/ajouté.
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const [, forceHistoryUpdate] = useState(0);
  const isApplyingHistory = useRef(false);
  const MAX_HISTORY = 50;
  const [zoom, setZoom] = useState(1);
  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 4;

  // Le zoom est un vrai transform:scale() sur le conteneur — les Rnd continuent
  // de calculer leurs positions/tailles sur les dimensions NON zoomées (cardWidthPx),
  // et reçoivent `scale={zoom}` pour convertir correctement les deltas de souris
  // (sinon un drag de 10px écran ne correspond plus à 10px de canvas une fois zoomé,
  // ce qui désynchronise complètement les éléments dès qu'on change de niveau).
  const cardWidthPx = template ? template.width_mm * MM_TO_PX : 324;
  const cardHeightPx = template ? template.height_mm * MM_TO_PX : 204;

  const zoomIn = () => setZoom(z => Math.min(MAX_ZOOM, Math.round((z + 0.25) * 100) / 100));
  const zoomOut = () => setZoom(z => Math.max(MIN_ZOOM, Math.round((z - 0.25) * 100) / 100));
  const zoomReset = () => setZoom(1);

  const fetchTemplate = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/cards/templates/${templateId}/`);
      setTemplate(res.data);
      const front = res.data.sides.find(s => s.side === 'front');
      const initialSide = front || res.data.sides[0];
      if (initialSide) {
        setActiveSideId(initialSide.id);
        setActiveSideKey(initialSide.side);
        setElements(initialSide.elements || []);
      }
      undoStack.current = [];
      redoStack.current = [];
    } catch {
      toast.error('Erreur lors du chargement du template.');
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => { fetchTemplate(); }, [fetchTemplate]);

  useEffect(() => {
    api.get('/cards/field-choices/').then(r => {
      if (r.data?.fields?.length) setFieldChoices(r.data.fields);
      if (r.data?.qr_sources?.length) setQrSources(r.data.qr_sources);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const churchId = isPlatformAdmin ? activeChurchId : undefined;
    api.get('/participants/', { params: { page_size: 50, church_id: churchId } }).then(r => {
      const list = Array.isArray(r.data) ? r.data : r.data.results || [];
      setParticipants(list);
    }).catch(() => {});
  }, [activeChurchId, isPlatformAdmin]);

  const clearHistory = () => {
    undoStack.current = [];
    redoStack.current = [];
    forceHistoryUpdate(n => n + 1);
  };

  const switchSide = (side) => {
    if (!template) return;
    const target = template.sides.find(s => s.side === side);
    if (target) {
      setActiveSideId(target.id);
      setActiveSideKey(side);
      setElements(target.elements || []);
      setSelectedId(null);
      setPreviewData(null);
      clearHistory();
      setZoom(1);
    }
  };

  const activeSide = template?.sides.find(s => s.id === activeSideId);

  const updateSideBackgroundColor = async (color) => {
    if (!activeSide) return;
    try {
      const res = await api.patch(`/cards/sides/${activeSide.id}/`, { background_color: color });
      setTemplate(prev => ({ ...prev, sides: prev.sides.map(s => s.id === activeSide.id ? res.data : s) }));
      setSaveError(false);
    } catch {
      setSaveError(true);
      toast.error("Erreur lors de l'enregistrement du fond.");
    }
  };

  const uploadSideBackground = async (file) => {
    if (!activeSide) return;
    setPendingSaves(n => n + 1);
    try {
      const fd = new FormData();
      fd.append('background_image', file);
      const res = await api.patch(`/cards/sides/${activeSide.id}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setTemplate(prev => ({ ...prev, sides: prev.sides.map(s => s.id === activeSide.id ? res.data : s) }));
      setSaveError(false);
    } catch {
      setSaveError(true);
      toast.error("Erreur lors de l'envoi de l'image de fond.");
    } finally {
      setPendingSaves(n => Math.max(0, n - 1));
    }
  };

  const removeSideBackground = async () => {
    if (!activeSide || !activeSide.background_image_url) return;
    try {
      const res = await api.patch(`/cards/sides/${activeSide.id}/`, { background_image: null });
      setTemplate(prev => ({ ...prev, sides: prev.sides.map(s => s.id === activeSide.id ? res.data : s) }));
      setSaveError(false);
    } catch {
      setSaveError(true);
      toast.error("Erreur lors de la suppression de l'image de fond.");
    }
  };

  const addBackSide = async () => {
    try {
      const res = await api.post('/cards/sides/', { template: template.id, side: 'back' });
      setTemplate(prev => ({ ...prev, sides: [...prev.sides, { ...res.data, elements: [] }] }));
      switchSideAfterCreate(res.data);
      toast.success('Verso ajouté.');
    } catch {
      toast.error("Erreur lors de l'ajout du verso.");
    }
  };
  const switchSideAfterCreate = (side) => {
    setActiveSideId(side.id);
    setActiveSideKey(side.side);
    setElements([]);
  };

  const selectedElement = elements.find(e => e.id === selectedId);

  const addElement = async (type) => {
    if (!activeSideId) return;
    const defaults = defaultsFor(type);
    try {
      const fd = new FormData();
      fd.append('side', activeSideId);
      fd.append('element_type', type === 'field' ? 'field' : type);
      fd.append('x', 30); fd.append('y', 30);
      fd.append('width', defaults.width); fd.append('height', defaults.height);
      if (defaults.content) fd.append('content', defaults.content);
      if (defaults.field_key) fd.append('field_key', defaults.field_key);
      fd.append('style', JSON.stringify(defaults.style || {}));
      const res = await api.post('/cards/elements/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setElements(prev => [...prev, res.data]);
      setSelectedId(res.data.id);
      pushHistory({ type: 'create', createdId: res.data.id });
    } catch {
      toast.error("Erreur lors de l'ajout de l'élément.");
    }
  };

  const addFieldElement = async (fieldKey, label) => {
    if (!activeSideId) return;
    const defaults = defaultsFor('field');
    try {
      const fd = new FormData();
      fd.append('side', activeSideId);
      fd.append('element_type', 'field');
      fd.append('field_key', fieldKey);
      fd.append('x', 30); fd.append('y', 30);
      fd.append('width', defaults.width); fd.append('height', defaults.height);
      fd.append('style', JSON.stringify(defaults.style));
      const res = await api.post('/cards/elements/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setElements(prev => [...prev, res.data]);
      setSelectedId(res.data.id);
      pushHistory({ type: 'create', createdId: res.data.id });
    } catch {
      toast.error("Erreur lors de l'ajout du champ.");
    }
  };

  // Ajoute directement une image configurée en filigrane : semi-transparente,
  // verrouillée et envoyée tout à l'arrière — il ne reste plus qu'à choisir
  // l'image/logo dans les propriétés.
  const addWatermarkElement = async () => {
    if (!activeSideId) return;
    const minZ = Math.min(0, ...elements.map(e => e.z_index));
    try {
      const fd = new FormData();
      fd.append('side', activeSideId);
      fd.append('element_type', 'image');
      fd.append('x', 10); fd.append('y', 10);
      fd.append('width', 80); fd.append('height', 80);
      fd.append('z_index', minZ - 1);
      fd.append('locked', true);
      fd.append('style', JSON.stringify({ opacity: 0.12, fit: 'contain' }));
      const res = await api.post('/cards/elements/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setElements(prev => [...prev, res.data]);
      setSelectedId(res.data.id);
      pushHistory({ type: 'create', createdId: res.data.id });
      toast('Filigrane ajouté — choisissez son image/logo dans les propriétés (déverrouillez-le pour le repositionner).', { icon: '💧' });
    } catch {
      toast.error("Erreur lors de l'ajout du filigrane.");
    }
  };

  // Ajoute directement un TEXTE configuré en filigrane : semi-transparent,
  // verrouillé et envoyé tout à l'arrière — idéal pour le nom de l'église.
  const addWatermarkTextElement = async () => {
    if (!activeSideId) return;
    const minZ = Math.min(0, ...elements.map(e => e.z_index));
    try {
      const fd = new FormData();
      fd.append('side', activeSideId);
      fd.append('element_type', 'text');
      fd.append('content', 'Nom de votre église');
      fd.append('x', 10); fd.append('y', 35);
      fd.append('width', 80); fd.append('height', 25);
      fd.append('z_index', minZ - 1);
      fd.append('locked', true);
      fd.append('style', JSON.stringify({ fontSize: 22, color: '#1e293b', fontWeight: 'bold', align: 'center', opacity: 0.15 }));
      const res = await api.post('/cards/elements/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setElements(prev => [...prev, res.data]);
      setSelectedId(res.data.id);
      pushHistory({ type: 'create', createdId: res.data.id });
      toast('Filigrane texte ajouté — modifiez le texte et l\'opacité dans les propriétés (déverrouillez-le pour le repositionner).', { icon: '💧' });
    } catch {
      toast.error("Erreur lors de l'ajout du filigrane texte.");
    }
  };

  // Mise à jour locale immédiate (fluidité du drag), sauvegarde différée au relâchement.
  const updateLocal = (id, patch) => {
    setElements(prev => prev.map(e => e.id === id ? { ...e, ...patch } : e));
  };

  const pushHistory = (entry) => {
    if (isApplyingHistory.current) return;
    undoStack.current = [...undoStack.current.slice(-(MAX_HISTORY - 1)), entry];
    redoStack.current = [];
    forceHistoryUpdate(n => n + 1);
  };

  // Enregistre dans l'historique un changement de champs sur un élément existant
  // (avant que le patch ne soit appliqué), puis applique + persiste normalement.
  const updateWithHistory = (id, patch) => {
    const el = elements.find(e => e.id === id);
    if (!el) return;
    const before = {};
    Object.keys(patch).forEach(k => { before[k] = el[k]; });
    pushHistory({ type: 'update', id, before, after: patch });
    updateLocal(id, patch);
    persistElement(id, patch);
  };

  // Applique l'effet inverse d'une entrée d'historique (utilisé par undo ET redo,
  // dans des directions opposées) sans re-pousser sur la pile qu'on est en train
  // de dépiler — isApplyingHistory le garantit.
  const applyHistoryEntry = async (entry, direction) => {
    isApplyingHistory.current = true;
    try {
      if (entry.type === 'update') {
        const patch = direction === 'undo' ? entry.before : entry.after;
        updateLocal(entry.id, patch);
        await persistElement(entry.id, patch);
      } else if (entry.type === 'delete') {
        if (direction === 'undo') {
          const created = await createElementFromSnapshot(entry.snapshot);
          if (created) entry.restoredId = created.id;
        } else if (entry.restoredId) {
          await api.delete(`/cards/elements/${entry.restoredId}/`);
          setElements(prev => prev.filter(e => e.id !== entry.restoredId));
        }
      } else if (entry.type === 'create') {
        if (direction === 'undo') {
          await api.delete(`/cards/elements/${entry.createdId}/`);
          setElements(prev => prev.filter(e => e.id !== entry.createdId));
          if (selectedId === entry.createdId) setSelectedId(null);
        }
        // Refaire une création n'est pas supporté (recréerait un doublon sans
        // ses réglages) — le redo sur un 'create' est donc un no-op volontaire.
      }
    } finally {
      isApplyingHistory.current = false;
    }
  };

  const handleUndo = async () => {
    const entry = undoStack.current[undoStack.current.length - 1];
    if (!entry) return;
    undoStack.current = undoStack.current.slice(0, -1);
    await applyHistoryEntry(entry, 'undo');
    redoStack.current = [...redoStack.current, entry];
    forceHistoryUpdate(n => n + 1);
  };

  const handleRedo = async () => {
    const entry = redoStack.current[redoStack.current.length - 1];
    if (!entry) return;
    redoStack.current = redoStack.current.slice(0, -1);
    await applyHistoryEntry(entry, 'redo');
    undoStack.current = [...undoStack.current, entry];
    forceHistoryUpdate(n => n + 1);
  };

  // Déplacement au clavier : un appui = petit pas (0.5%), Shift = pas large (5%).
  // Les appuis rapprochés (flèche maintenue) ne créent qu'UNE entrée d'historique,
  // capturée sur la position d'avant le tout premier appui de la rafale — sinon
  // Ctrl+Z n'annulerait qu'un pixel à la fois après un déplacement au clavier.
  const arrowMoveOrigin = useRef(null);
  const arrowMoveTimer = useRef(null);

  const nudgeSelected = (dx, dy) => {
    const el = elements.find(e => e.id === selectedId);
    if (!el || el.locked) return;
    if (!arrowMoveOrigin.current || arrowMoveOrigin.current.id !== el.id) {
      arrowMoveOrigin.current = { id: el.id, x: el.x, y: el.y };
    }
    const x = Math.max(0, Math.min(100, el.x + dx));
    const y = Math.max(0, Math.min(100, el.y + dy));
    updateLocal(el.id, { x, y });
    persistElement(el.id, { x, y });

    clearTimeout(arrowMoveTimer.current);
    arrowMoveTimer.current = setTimeout(() => {
      const origin = arrowMoveOrigin.current;
      arrowMoveOrigin.current = null;
      if (!origin) return;
      const current = elements.find(e => e.id === origin.id);
      if (!current || (current.x === origin.x && current.y === origin.y)) return;
      pushHistory({ type: 'update', id: origin.id, before: { x: origin.x, y: origin.y }, after: { x: current.x, y: current.y } });
    }, 500);
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return;
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        handleUndo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'y' || (e.shiftKey && e.key.toLowerCase() === 'z'))) {
        e.preventDefault();
        handleRedo();
      } else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && selectedId) {
        e.preventDefault();
        const step = e.shiftKey ? 5 : 0.5;
        const deltas = { ArrowUp: [0, -step], ArrowDown: [0, step], ArrowLeft: [-step, 0], ArrowRight: [step, 0] };
        nudgeSelected(...deltas[e.key]);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [elements, selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const persistElement = async (id, patch) => {
    setPendingSaves(n => n + 1);
    try {
      const fd = new FormData();
      Object.entries(patch).forEach(([k, v]) => {
        if (k === 'style') fd.append(k, JSON.stringify(v));
        else fd.append(k, v);
      });
      await api.patch(`/cards/elements/${id}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSaveError(false);
    } catch {
      setSaveError(true);
      toast.error("Erreur lors de l'enregistrement — vos derniers changements n'ont pas été sauvegardés.");
    } finally {
      setPendingSaves(n => Math.max(0, n - 1));
    }
  };

  const updateStyle = (id, styleKey, value) => {
    const el = elements.find(e => e.id === id);
    if (!el) return;
    const nextStyle = { ...(el.style || {}), [styleKey]: value };
    updateWithHistory(id, { style: nextStyle });
  };

  const updateContent = (id, content) => {
    updateWithHistory(id, { content });
  };

  const updateFieldKey = (id, field_key) => {
    updateWithHistory(id, { field_key });
  };

  const uploadImageFile = async (id, file) => {
    setPendingSaves(n => n + 1);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.patch(`/cards/elements/${id}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      updateLocal(id, { image_url: res.data.image_url });
      setSaveError(false);
    } catch {
      setSaveError(true);
      toast.error("Erreur lors de l'envoi de l'image.");
    } finally {
      setPendingSaves(n => Math.max(0, n - 1));
    }
  };

  const markAsWatermark = (el) => {
    const minZ = Math.min(0, ...elements.map(e => e.z_index));
    // L'opacité par défaut d'une image est 1 — `??` ne la remplacerait donc jamais
    // par 0.12 (1 ?? 0.12 === 1). On force un minimum avec Math.min pour que le
    // filigrane soit réellement semi-transparent même si l'image était opaque.
    const nextStyle = { ...(el.style || {}), opacity: Math.min(Number(el.style?.opacity ?? 1), 0.12) };
    updateWithHistory(el.id, { z_index: minZ - 1, locked: true, style: nextStyle });
  };

  // Suppression : on garde une copie complète pour pouvoir la recréer (nouvel ID)
  // si l'utilisateur fait Ctrl+Z — l'API ne permet pas de restaurer un ID précis.
  const deleteElement = async (id) => {
    const el = elements.find(e => e.id === id);
    try {
      await api.delete(`/cards/elements/${id}/`);
      setElements(prev => prev.filter(e => e.id !== id));
      if (selectedId === id) setSelectedId(null);
      if (el) pushHistory({ type: 'delete', snapshot: el });
    } catch {
      toast.error('Erreur lors de la suppression.');
    }
  };

  const createElementFromSnapshot = async (snapshot) => {
    try {
      const fd = new FormData();
      fd.append('side', activeSideId);
      fd.append('element_type', snapshot.element_type);
      fd.append('field_key', snapshot.field_key || '');
      fd.append('content', snapshot.content || '');
      fd.append('x', snapshot.x); fd.append('y', snapshot.y);
      fd.append('width', snapshot.width); fd.append('height', snapshot.height);
      fd.append('rotation', snapshot.rotation || 0);
      fd.append('z_index', snapshot.z_index || 0);
      fd.append('locked', !!snapshot.locked); fd.append('visible', snapshot.visible !== false);
      fd.append('style', JSON.stringify(snapshot.style || {}));
      const res = await api.post('/cards/elements/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setElements(prev => [...prev, res.data]);
      setSelectedId(res.data.id);
      return res.data;
    } catch {
      toast.error("Erreur lors de la restauration de l'élément.");
      return null;
    }
  };

  const duplicateElement = async (el) => {
    try {
      const fd = new FormData();
      fd.append('side', activeSideId);
      fd.append('element_type', el.element_type);
      fd.append('field_key', el.field_key || '');
      fd.append('content', el.content || '');
      fd.append('x', Math.min(90, el.x + 4)); fd.append('y', Math.min(90, el.y + 4));
      fd.append('width', el.width); fd.append('height', el.height);
      fd.append('rotation', el.rotation);
      fd.append('style', JSON.stringify(el.style || {}));
      const res = await api.post('/cards/elements/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setElements(prev => [...prev, res.data]);
      setSelectedId(res.data.id);
      pushHistory({ type: 'create', createdId: res.data.id });
    } catch {
      toast.error('Erreur lors de la duplication.');
    }
  };

  const toggleLock = (el) => {
    updateWithHistory(el.id, { locked: !el.locked });
  };
  const toggleVisible = (el) => {
    updateWithHistory(el.id, { visible: !el.visible });
  };

  const bringToFront = (el) => {
    const maxZ = Math.max(0, ...elements.map(e => e.z_index));
    updateWithHistory(el.id, { z_index: maxZ + 1 });
  };
  const sendToBack = (el) => {
    const minZ = Math.min(0, ...elements.map(e => e.z_index));
    updateWithHistory(el.id, { z_index: minZ - 1 });
  };
  const moveStep = (el, delta) => {
    updateWithHistory(el.id, { z_index: el.z_index + delta });
  };

  const loadPreview = async () => {
    if (!previewParticipantId) { setPreviewData(null); return; }
    try {
      const res = await api.get(`/cards/preview/${templateId}/${previewParticipantId}/`, { params: { stage: 'draft' } });
      setPreviewData(res.data);
    } catch {
      toast.error('Erreur lors de la prévisualisation.');
    }
  };

  // Chaque changement est déjà envoyé au serveur immédiatement (persistElement).
  // "Enregistrer" vérifie explicitement que tout est bien sync en relisant le
  // serveur, pour donner une confirmation fiable plutôt que de faire confiance
  // silencieusement à l'état local.
  const handleSave = async () => {
    if (pendingSaves > 0) {
      toast('Enregistrement en cours, patientez un instant...', { icon: '⏳' });
      return;
    }
    setSaving(true);
    try {
      await fetchTemplate();
      toast.success('Toutes les modifications sont enregistrées.');
    } catch {
      toast.error("Impossible de confirmer l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndPreview = async () => {
    await handleSave();
    if (previewParticipantId) await loadPreview();
    else toast('Sélectionnez un fidèle pour prévisualiser.', { icon: 'ℹ️' });
  };

  const handleCancel = async () => {
    setConfirmCancel(false);
    await fetchTemplate();
    setPreviewData(null);
    toast.success('Modifications non enregistrées annulées — dernière version serveur rechargée.');
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const res = await api.post(`/cards/templates/${templateId}/publish/`);
      setTemplate(prev => ({ ...prev, has_published_version: true, published_at: res.data.published_at }));
      toast.success('Template publié — toutes les nouvelles cartes utiliseront cette disposition.');
    } catch {
      toast.error('Erreur lors de la publication.');
    } finally {
      setPublishing(false);
      setConfirmPublish(false);
    }
  };

  const openVersions = async () => {
    setShowVersions(true);
    setVersionsLoading(true);
    try {
      const res = await api.get(`/cards/templates/${templateId}/versions/`);
      setVersions(res.data);
    } catch {
      toast.error("Erreur lors du chargement de l'historique.");
    } finally {
      setVersionsLoading(false);
    }
  };

  const handleRollback = async (versionId) => {
    try {
      const res = await api.post(`/cards/templates/${templateId}/rollback/${versionId}/`);
      setTemplate(prev => ({ ...prev, published_at: res.data.published_at }));
      toast.success('Version restaurée — les cartes des fidèles utilisent maintenant cette disposition.');
      setShowVersions(false);
      setConfirmRollback(null);
    } catch {
      toast.error('Erreur lors de la restauration.');
    }
  };

  const previewElementsForSide = () => {
    if (!previewData) return null;
    const side = previewData.sides.find(s => s.id === activeSideId);
    return side ? side.elements : null;
  };

  if (loading) {
    return <div style={{ padding: 40, color: 'var(--text-secondary)' }}>Chargement...</div>;
  }
  if (!template) {
    return <div style={{ padding: 40, color: 'var(--text-secondary)' }}>Template introuvable. <button onClick={() => navigate('/cartes')} style={BTN_GHOST}>Retour</button></div>;
  }

  const previewSideElements = previewElementsForSide();

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <button onClick={() => navigate('/cartes')} style={{ ...BTN_GHOST, padding: '6px 10px', fontSize: '0.78rem', marginBottom: 6 }}>← Retour</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h1 style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>{template.name}</h1>
            {template.has_published_version ? (
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#166534', background: '#f0fdf4', borderRadius: 5, padding: '3px 8px' }}>PUBLIÉ</span>
            ) : (
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#9a3412', background: '#fff7ed', borderRadius: 5, padding: '3px 8px' }}>JAMAIS PUBLIÉ</span>
            )}
          </div>
          <p style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', margin: '4px 0 0' }}>
            Vous éditez le brouillon — les cartes déjà utilisées continuent d'afficher {template.has_published_version ? 'la dernière version publiée' : 'aucune disposition (template pas encore publié)'} tant que vous ne cliquez pas sur « Publier ».
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: '0.74rem', fontWeight: 600 }}>
            {pendingSaves > 0 ? (
              <span style={{ color: '#9a3412' }}>⏳ Enregistrement en cours...</span>
            ) : saveError ? (
              <span style={{ color: '#b91c1c' }}>⚠ Dernière sauvegarde échouée</span>
            ) : (
              <span style={{ color: '#166534' }}>✓ Toutes les modifications sont enregistrées</span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 7, overflow: 'hidden' }}>
            <button onClick={() => switchSide('front')} style={{ padding: '7px 14px', border: 'none', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', background: activeSideKey === 'front' ? '#1e293b' : '#fff', color: activeSideKey === 'front' ? '#fff' : 'var(--text-secondary)' }}>Recto</button>
            {template.sides.find(s => s.side === 'back') ? (
              <button onClick={() => switchSide('back')} style={{ padding: '7px 14px', border: 'none', borderLeft: '1px solid var(--border)', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', background: activeSideKey === 'back' ? '#1e293b' : '#fff', color: activeSideKey === 'back' ? '#fff' : 'var(--text-secondary)' }}>Verso</button>
            ) : (
              <button onClick={addBackSide} style={{ padding: '7px 14px', border: 'none', borderLeft: '1px solid var(--border)', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', background: '#fff', color: 'var(--text-secondary)' }}>+ Verso</button>
            )}
          </div>
          <select value={previewParticipantId} onChange={e => { setPreviewParticipantId(e.target.value); }} style={{ ...INPUT, width: 200 }}>
            <option value="">— Aperçu réel : choisir un fidèle —</option>
            {participants.map(p => <option key={p.id} value={p.id}>{p.full_name || `${p.first_name} ${p.last_name}`}</option>)}
          </select>
          <button onClick={loadPreview} disabled={!previewParticipantId} style={{ ...BTN_DARK, padding: '8px 16px', fontSize: '0.8rem' }}>Prévisualiser</button>
          {previewData && <button onClick={() => { setPreviewData(null); setPreviewParticipantId(''); }} style={{ ...BTN_GHOST, padding: '8px 12px', fontSize: '0.8rem' }}>✕ Aperçu</button>}
          <button onClick={handleUndo} disabled={undoStack.current.length === 0} title="Annuler (Ctrl+Z)"
            style={{ ...BTN_GHOST, padding: '8px 10px', fontSize: '0.8rem', opacity: undoStack.current.length === 0 ? 0.4 : 1 }}>↶</button>
          <button onClick={handleRedo} disabled={redoStack.current.length === 0} title="Rétablir (Ctrl+Y)"
            style={{ ...BTN_GHOST, padding: '8px 10px', fontSize: '0.8rem', opacity: redoStack.current.length === 0 ? 0.4 : 1 }}>↷</button>
          <button onClick={() => setConfirmCancel(true)} style={{ ...BTN_GHOST, padding: '8px 14px', fontSize: '0.8rem' }}>Annuler les modifications</button>
          <button onClick={handleSave} disabled={saving || pendingSaves > 0} style={{ ...BTN_DARK, padding: '8px 16px', fontSize: '0.8rem' }}>
            {saving ? 'Enregistrement...' : '💾 Enregistrer'}
          </button>
          <button onClick={handleSaveAndPreview} disabled={saving || pendingSaves > 0} style={{ background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: 700, cursor: 'pointer', padding: '8px 16px', fontSize: '0.8rem' }}>
            Enregistrer et prévisualiser
          </button>
          <button onClick={() => setConfirmPublish(true)} disabled={publishing}
            style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: 700, cursor: 'pointer', padding: '8px 18px', fontSize: '0.8rem' }}>
            {publishing ? 'Publication...' : '📢 Publier'}
          </button>
          {template.has_published_version && (
            <button onClick={openVersions} style={{ ...BTN_GHOST, padding: '8px 14px', fontSize: '0.8rem' }}>
              🕘 Historique
            </button>
          )}
        </div>
      </div>

      {showVersions && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setShowVersions(false)}>
          <div style={{ background: 'var(--card-bg)', borderRadius: 12, padding: 20, width: '100%', maxWidth: 420, maxHeight: '70vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Historique des versions publiées</span>
              <button onClick={() => setShowVersions(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: 'var(--text-secondary)' }}>×</button>
            </div>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 0 }}>
              Restaurer une version remplace immédiatement la carte utilisée par les fidèles — le brouillon en cours d'édition n'est pas affecté.
            </p>
            {versionsLoading ? (
              <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>Chargement...</p>
            ) : versions.length === 0 ? (
              <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>Aucune version antérieure — ce template n'a été publié qu'une seule fois.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {versions.map(v => (
                  <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 8 }}>
                    <div>
                      <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                        Publiée le {v.published_at ? new Date(v.published_at).toLocaleString('fr-FR') : '—'}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                        Remplacée le {new Date(v.created_at).toLocaleString('fr-FR')}
                      </div>
                    </div>
                    <button onClick={() => setConfirmRollback(v.id)} style={{ ...BTN_GHOST, padding: '6px 12px', fontSize: '0.76rem' }}>
                      Restaurer
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirmRollback}
        title="Restaurer cette version ?"
        message="La version actuellement publiée sera elle-même sauvegardée dans l'historique (vous pourrez y revenir), puis remplacée par la version sélectionnée. Effectif immédiatement pour toutes les cartes des fidèles."
        confirmLabel="Restaurer"
        confirmColor="#1d4ed8"
        onConfirm={() => handleRollback(confirmRollback)}
        onCancel={() => setConfirmRollback(null)}
      />

      <ConfirmModal
        open={confirmPublish}
        title="Publier ce template ?"
        message={
          template.has_published_version
            ? "La disposition actuellement publiée sera remplacée par votre brouillon. Toutes les nouvelles cartes générées avec ce template utiliseront cette nouvelle version."
            : "Ce template n'a encore jamais été publié — après cette action, il pourra être utilisé pour générer de vraies cartes."
        }
        confirmLabel="Publier"
        confirmColor="#16a34a"
        onConfirm={handlePublish}
        onCancel={() => setConfirmPublish(false)}
      />

      <ConfirmModal
        open={confirmCancel}
        title="Annuler les modifications ?"
        message="Toute modification qui n'aurait pas encore été confirmée sera perdue — le template sera rechargé depuis la dernière version enregistrée sur le serveur."
        confirmLabel="Annuler mes modifications"
        onConfirm={handleCancel}
        onCancel={() => setConfirmCancel(false)}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '210px 1fr 260px', gap: 14, alignItems: 'start' }}>
        {/* ── Panneau éléments ── */}
        <div style={{ ...PANEL, padding: 14 }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Ajouter</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
            {ADD_ITEMS.map(item => (
              <button key={item.type} onClick={() => addElement(item.type)}
                style={{ ...BTN_GHOST, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', fontSize: '0.8rem', textAlign: 'left' }}>
                <span style={{ fontSize: 15 }}>{item.icon}</span> {item.label}
              </button>
            ))}
            <button onClick={addWatermarkElement}
              style={{ ...BTN_GHOST, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', fontSize: '0.8rem', textAlign: 'left', borderColor: '#93c5fd', color: '#1d4ed8' }}>
              <span style={{ fontSize: 15 }}>💧</span> Filigrane image (logo)
            </button>
            <button onClick={addWatermarkTextElement}
              style={{ ...BTN_GHOST, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', fontSize: '0.8rem', textAlign: 'left', borderColor: '#93c5fd', color: '#1d4ed8' }}>
              <span style={{ fontSize: 15 }}>🔤</span> Filigrane texte
            </button>
          </div>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Champs du fidèle</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 220, overflowY: 'auto' }}>
            {fieldChoices.map(f => (
              <button key={f.key} onClick={() => addFieldElement(f.key, f.label)}
                style={{ ...BTN_GHOST, padding: '6px 9px', fontSize: '0.76rem', textAlign: 'left' }}>
                + {f.label}
              </button>
            ))}
          </div>

          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 12 }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Fond du {activeSideKey === 'front' ? 'recto' : 'verso'}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ ...LABEL, margin: 0 }}>Couleur</label>
                <input type="color" value={activeSide?.background_color || '#ffffff'}
                  onChange={e => updateSideBackgroundColor(e.target.value)}
                  style={{ width: 40, height: 30, padding: 2, borderRadius: 6, border: '1px solid var(--border)', cursor: 'pointer', background: 'var(--card-bg)' }} />
              </div>
              <div>
                <label style={LABEL}>Image de fond</label>
                <input type="file" accept="image/*" onChange={e => e.target.files[0] && uploadSideBackground(e.target.files[0])} style={{ fontSize: '0.74rem' }} />
                {activeSide?.background_image_url && (
                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <img src={activeSide.background_image_url} alt="" style={{ width: 70, height: 44, objectFit: 'cover', borderRadius: 5, border: '1px solid var(--border)' }} />
                    <button onClick={removeSideBackground} style={{ ...BTN_GHOST, padding: '5px 9px', fontSize: '0.72rem' }}>Retirer</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Canvas ── */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, width: '100%', overflow: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, position: 'sticky', left: 0 }}>
            <button onClick={zoomOut} disabled={zoom <= MIN_ZOOM} title="Zoom arrière" style={{ ...BTN_GHOST, padding: '6px 11px', fontSize: '0.9rem', opacity: zoom <= MIN_ZOOM ? 0.4 : 1 }}>−</button>
            <button onClick={zoomReset} title="Réinitialiser le zoom" style={{ ...BTN_GHOST, padding: '6px 10px', fontSize: '0.76rem', minWidth: 52 }}>{Math.round(zoom * 100)}%</button>
            <button onClick={zoomIn} disabled={zoom >= MAX_ZOOM} title="Zoom avant" style={{ ...BTN_GHOST, padding: '6px 11px', fontSize: '0.9rem', opacity: zoom >= MAX_ZOOM ? 0.4 : 1 }}>+</button>
          </div>
          <div
            onWheel={(e) => {
              if (!e.ctrlKey) return;
              e.preventDefault();
              if (e.deltaY < 0) zoomIn(); else zoomOut();
            }}
            style={{ width: '100%', overflow: 'auto', display: 'flex', justifyContent: 'center', padding: '4px 0' }}
          >
            {/* Wrapper de taille réelle zoomée : réserve l'espace dans le flux
                (scroll/centrage), le scale() du canvas ne change pas la mise en page. */}
            <div style={{ width: cardWidthPx * zoom, height: cardHeightPx * zoom, flexShrink: 0 }}>
              <div ref={canvasRef} onClick={() => setSelectedId(null)} style={{
                position: 'relative', width: cardWidthPx, height: cardHeightPx, flexShrink: 0,
                transform: `scale(${zoom})`, transformOrigin: 'top left',
                background: activeSide?.background_image_url
                  ? `url(${activeSide.background_image_url}) center/cover`
                  : (activeSide?.background_color || '#ffffff'),
                borderRadius: 10, boxShadow: '0 8px 30px rgba(15,23,42,.12)', border: '1px solid var(--border)', overflow: 'hidden',
              }}>
            {elements.filter(e => e.visible !== false).map(el => {
              const previewEl = previewSideElements?.find(p => p.id === el.id);
              const resolved = previewEl?.resolved_value;
              const resolvedColor = previewEl?.resolved_color;
              return (
                <Rnd
                  key={el.id}
                  size={{ width: (el.width / 100) * cardWidthPx, height: (el.height / 100) * cardHeightPx }}
                  position={{ x: (el.x / 100) * cardWidthPx, y: (el.y / 100) * cardHeightPx }}
                  scale={zoom}
                  disableDragging={el.locked}
                  enableResizing={!el.locked}
                  bounds="parent"
                  style={{ zIndex: el.z_index, outline: selectedId === el.id ? '2px solid #1e40af' : 'none' }}
                  onDragStart={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); setSelectedId(el.id); }}
                  onDragStop={(e, d) => {
                    const x = (d.x / cardWidthPx) * 100;
                    const y = (d.y / cardHeightPx) * 100;
                    if (x === el.x && y === el.y) return;
                    updateWithHistory(el.id, { x, y });
                  }}
                  onResizeStop={(e, dir, ref, delta, pos) => {
                    const width = (ref.offsetWidth / cardWidthPx) * 100;
                    const height = (ref.offsetHeight / cardHeightPx) * 100;
                    const x = (pos.x / cardWidthPx) * 100;
                    const y = (pos.y / cardHeightPx) * 100;
                    updateWithHistory(el.id, { width, height, x, y });
                  }}
                >
                  <div style={{ width: '100%', height: '100%', cursor: el.locked ? 'not-allowed' : 'move', transform: `rotate(${el.rotation || 0}deg)` }}>
                    <CardElementContent
                      el={el}
                      resolvedValue={resolved}
                      resolvedColor={resolvedColor}
                      fieldLabel={fieldChoices.find(f => f.key === el.field_key)?.label}
                      cardWidthPx={cardWidthPx}
                    />
                  </div>
                </Rnd>
              );
            })}
              </div>
            </div>
          </div>
          <div style={{ fontSize: '0.76rem', color: 'var(--text-secondary)' }}>
            {template.width_mm}mm × {template.height_mm}mm — {activeSideKey === 'front' ? 'Recto' : 'Verso'}
          </div>
        </div>

        {/* ── Panneau propriétés + calques ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ ...PANEL, padding: 14 }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Propriétés</div>
            {!selectedElement ? (
              <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: 0 }}>Sélectionnez un élément pour modifier ses propriétés.</p>
            ) : (
              <ElementProperties
                element={selectedElement}
                fieldChoices={fieldChoices}
                qrSources={qrSources}
                onUpdateStyle={(k, v) => updateStyle(selectedElement.id, k, v)}
                onUpdateContent={(v) => updateContent(selectedElement.id, v)}
                onUpdateFieldKey={(v) => updateFieldKey(selectedElement.id, v)}
                onUpdatePosition={(patch) => updateWithHistory(selectedElement.id, patch)}
                onDelete={() => deleteElement(selectedElement.id)}
                onDuplicate={() => duplicateElement(selectedElement)}
                onToggleLock={() => toggleLock(selectedElement)}
                onToggleVisible={() => toggleVisible(selectedElement)}
                onUploadImage={(file) => uploadImageFile(selectedElement.id, file)}
                onMarkWatermark={() => markAsWatermark(selectedElement)}
                onBringToFront={() => bringToFront(selectedElement)}
                onSendToBack={() => sendToBack(selectedElement)}
              />
            )}
          </div>

          <div style={{ ...PANEL, padding: 14 }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Calques</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 260, overflowY: 'auto' }}>
              {[...elements].sort((a, b) => b.z_index - a.z_index).map(el => (
                <div key={el.id} onClick={() => setSelectedId(el.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', borderRadius: 6, cursor: 'pointer', background: selectedId === el.id ? '#eff6ff' : 'transparent', fontSize: '0.74rem', opacity: el.visible === false ? 0.5 : 1 }}>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-primary)', fontWeight: 600 }}>
                    {el.element_type === 'field' ? (fieldChoices.find(f => f.key === el.field_key)?.label || el.field_key) : el.element_type === 'text' ? (el.content || 'Texte') : el.element_type}
                  </span>
                  <button onClick={(e) => { e.stopPropagation(); bringToFront(el); }} title="Premier plan" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11 }}>⏫</button>
                  <button onClick={(e) => { e.stopPropagation(); sendToBack(el); }} title="Arrière-plan" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11 }}>⏬</button>
                  <button onClick={(e) => { e.stopPropagation(); moveStep(el, 1); }} title="Monter d'un cran" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11 }}>▲</button>
                  <button onClick={(e) => { e.stopPropagation(); moveStep(el, -1); }} title="Descendre d'un cran" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11 }}>▼</button>
                  <button onClick={(e) => { e.stopPropagation(); toggleVisible(el); }} title="Masquer/afficher" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11 }}>{el.visible === false ? '🚫' : '👁'}</button>
                  <button onClick={(e) => { e.stopPropagation(); toggleLock(el); }} title="Verrouiller" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11 }}>{el.locked ? '🔒' : '🔓'}</button>
                </div>
              ))}
              {elements.length === 0 && <p style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', margin: 0 }}>Aucun élément.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function OpacitySlider({ value, onChange }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <label style={LABEL}>Opacité</label>
        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary)' }}>{Math.round(value * 100)}%</span>
      </div>
      <input type="range" min="0" max="100" value={Math.round(value * 100)}
        onChange={e => onChange(Number(e.target.value) / 100)}
        style={{ width: '100%', accentColor: '#1d4ed8', cursor: 'pointer' }} />
    </div>
  );
}

function ElementProperties({ element, fieldChoices, qrSources, onUpdateStyle, onUpdateContent, onUpdateFieldKey, onUpdatePosition, onDelete, onDuplicate, onToggleLock, onToggleVisible, onUploadImage, onMarkWatermark, onBringToFront, onSendToBack }) {
  const style = element.style || {};
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {element.element_type === 'text' && (
        <div>
          <label style={LABEL}>Contenu</label>
          <textarea value={element.content || ''} onChange={e => onUpdateContent(e.target.value)} rows={2} style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }} />
        </div>
      )}

      {element.element_type === 'field' && (
        <div>
          <label style={LABEL}>Champ dynamique</label>
          <select value={element.field_key || ''} onChange={e => onUpdateFieldKey(e.target.value)} style={INPUT}>
            {fieldChoices.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: '0.82rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!style.showLabel} onChange={e => onUpdateStyle('showLabel', e.target.checked)} />
            Afficher le libellé (ex : « Nom : Fofana »)
          </label>
        </div>
      )}

      {element.element_type === 'qrcode' && (
        <div>
          <label style={LABEL}>Donnée encodée</label>
          <select value={element.field_key || 'member_code'} onChange={e => onUpdateFieldKey(e.target.value)} style={INPUT}>
            {qrSources.map(q => <option key={q.key} value={q.key}>{q.label}</option>)}
          </select>
        </div>
      )}

      {(element.element_type === 'text' || element.element_type === 'field') && (
        <>
          <div>
            <label style={LABEL}>Police</label>
            <select value={style.fontFamily || ''} onChange={e => onUpdateStyle('fontFamily', e.target.value)} style={INPUT}>
              <option value="">Par défaut (système)</option>
              <option value="'Georgia', serif">Georgia (sérif)</option>
              <option value="'Times New Roman', serif">Times New Roman</option>
              <option value="'Palatino Linotype', serif">Palatino</option>
              <option value="'Courier New', monospace">Courier New</option>
              <option value="'Verdana', sans-serif">Verdana</option>
              <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
              <option value="'Arial', sans-serif">Arial</option>
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <label style={LABEL}>Taille police</label>
              <input type="number" value={style.fontSize || 14} onChange={e => onUpdateStyle('fontSize', Number(e.target.value))} style={INPUT} />
            </div>
            <div>
              <label style={LABEL}>Couleur</label>
              <input type="color" value={style.color || '#1e293b'} disabled={style.colorSource === 'status'} onChange={e => onUpdateStyle('color', e.target.value)} style={{ ...INPUT, padding: 2, height: 32, opacity: style.colorSource === 'status' ? 0.5 : 1 }} />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.76rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={style.colorSource === 'status'} onChange={e => onUpdateStyle('colorSource', e.target.checked ? 'status' : undefined)} />
            Couleur automatique selon le statut du fidèle (Réglages → couleurs par catégorie)
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => onUpdateStyle('fontWeight', style.fontWeight === 'bold' ? 'normal' : 'bold')} style={{ ...BTN_GHOST, flex: 1, padding: '6px', fontWeight: style.fontWeight === 'bold' ? 800 : 400, background: style.fontWeight === 'bold' ? '#1e293b' : undefined, color: style.fontWeight === 'bold' ? '#fff' : undefined }}>G</button>
            <button onClick={() => onUpdateStyle('italic', !style.italic)} style={{ ...BTN_GHOST, flex: 1, padding: '6px', fontStyle: 'italic', background: style.italic ? '#1e293b' : undefined, color: style.italic ? '#fff' : undefined }}>I</button>
          </div>
          <div>
            <label style={LABEL}>Alignement</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {['left', 'center', 'right'].map(a => (
                <button key={a} onClick={() => onUpdateStyle('align', a)} style={{ ...BTN_GHOST, flex: 1, padding: '6px', background: style.align === a ? '#1e293b' : undefined, color: style.align === a ? '#fff' : undefined }}>
                  {a === 'left' ? '⯇' : a === 'center' ? '☰' : '⯈'}
                </button>
              ))}
            </div>
          </div>
          <OpacitySlider value={style.opacity ?? 1} onChange={v => onUpdateStyle('opacity', v)} />
        </>
      )}

      {element.element_type === 'photo' && (
        <>
          <div>
            <label style={LABEL}>Forme</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => onUpdateStyle('borderRadius', 'square')} style={{ ...BTN_GHOST, flex: 1, padding: '6px', background: style.borderRadius !== 'circle' ? '#1e293b' : undefined, color: style.borderRadius !== 'circle' ? '#fff' : undefined }}>Carrée</button>
              <button onClick={() => onUpdateStyle('borderRadius', 'circle')} style={{ ...BTN_GHOST, flex: 1, padding: '6px', background: style.borderRadius === 'circle' ? '#1e293b' : undefined, color: style.borderRadius === 'circle' ? '#fff' : undefined }}>Ronde</button>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <label style={LABEL}>Bordure (px)</label>
              <input type="number" value={style.borderWidth || 0} onChange={e => onUpdateStyle('borderWidth', Number(e.target.value))} style={INPUT} />
            </div>
            <div>
              <label style={LABEL}>Couleur bordure</label>
              <input type="color" value={style.borderColor || '#1e40af'} onChange={e => onUpdateStyle('borderColor', e.target.value)} style={{ ...INPUT, padding: 2, height: 32 }} />
            </div>
          </div>
        </>
      )}

      {element.element_type === 'image' && (
        <>
          <div>
            <label style={LABEL}>Image</label>
            <input type="file" accept="image/*" onChange={e => e.target.files[0] && onUploadImage(e.target.files[0])} style={{ fontSize: '0.78rem' }} />
            {element.image_url && <img src={element.image_url} alt="" style={{ maxWidth: '100%', maxHeight: 60, marginTop: 6, borderRadius: 4, opacity: element.style?.opacity ?? 1 }} />}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <label style={LABEL}>Ajustement</label>
              <select value={style.fit || 'cover'} onChange={e => onUpdateStyle('fit', e.target.value)} style={INPUT}>
                <option value="cover">Remplir</option>
                <option value="contain">Contenir</option>
              </select>
            </div>
            <OpacitySlider value={style.opacity ?? 1} onChange={v => onUpdateStyle('opacity', v)} />
          </div>
          <button onClick={onMarkWatermark} style={{ ...BTN_GHOST, padding: '7px', fontSize: '0.78rem', borderColor: '#93c5fd', color: '#1d4ed8', fontWeight: 600 }}>
            💧 Marquer comme filigrane (arrière-plan, verrouillé, opacité réduite)
          </button>
        </>
      )}

      {element.element_type === 'shape' && (
        <>
          <div>
            <label style={LABEL}>Type de forme</label>
            <select value={style.shape || 'rectangle'} onChange={e => onUpdateStyle('shape', e.target.value)} style={INPUT}>
              <option value="rectangle">Rectangle</option>
              <option value="circle">Cercle</option>
              <option value="line">Ligne</option>
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <label style={LABEL}>Couleur</label>
              <input type="color" value={style.background || '#e2e8f0'} disabled={style.colorSource === 'status'} onChange={e => onUpdateStyle('background', e.target.value)} style={{ ...INPUT, padding: 2, height: 32, opacity: style.colorSource === 'status' ? 0.5 : 1 }} />
            </div>
            <div>
              <label style={LABEL}>Bordure</label>
              <input type="color" value={style.borderColor || '#94a3b8'} onChange={e => onUpdateStyle('borderColor', e.target.value)} style={{ ...INPUT, padding: 2, height: 32 }} />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.76rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={style.colorSource === 'status'} onChange={e => onUpdateStyle('colorSource', e.target.checked ? 'status' : undefined)} />
            Couleur automatique selon le statut du fidèle
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <label style={LABEL}>Épaisseur bordure</label>
              <input type="number" value={style.borderWidth || 0} onChange={e => onUpdateStyle('borderWidth', Number(e.target.value))} style={INPUT} />
            </div>
            <OpacitySlider value={style.opacity ?? 1} onChange={v => onUpdateStyle('opacity', v)} />
          </div>
        </>
      )}

      {element.element_type === 'qrcode' && (
        <div>
          <label style={LABEL}>Couleur QR</label>
          <input type="color" value={style.fgColor || '#000000'} onChange={e => onUpdateStyle('fgColor', e.target.value)} style={{ ...INPUT, padding: 2, height: 32 }} />
        </div>
      )}

      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
          <div>
            <label style={LABEL}>X (%)</label>
            <input type="number" value={Math.round(element.x)} onChange={e => onUpdatePosition({ x: Number(e.target.value) })} style={INPUT} />
          </div>
          <div>
            <label style={LABEL}>Y (%)</label>
            <input type="number" value={Math.round(element.y)} onChange={e => onUpdatePosition({ y: Number(e.target.value) })} style={INPUT} />
          </div>
          <div>
            <label style={LABEL}>Largeur (%)</label>
            <input type="number" value={Math.round(element.width)} onChange={e => onUpdatePosition({ width: Number(e.target.value) })} style={INPUT} />
          </div>
          <div>
            <label style={LABEL}>Hauteur (%)</label>
            <input type="number" value={Math.round(element.height)} onChange={e => onUpdatePosition({ height: Number(e.target.value) })} style={INPUT} />
          </div>
        </div>
        <div>
          <label style={LABEL}>Rotation (°)</label>
          <input type="number" value={element.rotation || 0} onChange={e => onUpdatePosition({ rotation: Number(e.target.value) })} style={INPUT} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
        <button onClick={onBringToFront} style={{ ...BTN_GHOST, padding: '7px', fontSize: '0.75rem' }}>⬆ Premier plan</button>
        <button onClick={onSendToBack} style={{ ...BTN_GHOST, padding: '7px', fontSize: '0.75rem' }}>⬇ Arrière-plan</button>
        <button onClick={onDuplicate} style={{ ...BTN_GHOST, padding: '7px', fontSize: '0.75rem' }}>Dupliquer</button>
        <button onClick={onToggleLock} style={{ ...BTN_GHOST, padding: '7px', fontSize: '0.75rem' }}>{element.locked ? 'Déverrouiller' : 'Verrouiller'}</button>
        <button onClick={onToggleVisible} style={{ ...BTN_GHOST, padding: '7px', fontSize: '0.75rem' }}>{element.visible === false ? 'Afficher' : 'Masquer'}</button>
        <button onClick={onDelete} style={{ background: '#fef2f2', color: '#b91c1c', border: 'none', borderRadius: 6, padding: '7px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}>Supprimer</button>
      </div>
    </div>
  );
}
